import { useState } from 'react'
import { Link } from 'react-router-dom'
import MockDataBanner from '../components/finance/MockDataBanner'
import { isFantasyUsingMockData } from '../lib/fantasy-api'
import { useFantasy } from '../components/fantasy-football/useFantasy'
import OverviewTab from '../components/fantasy-football/OverviewTab'
import LeaguesTab from '../components/fantasy-football/LeaguesTab'
import PlayersTab from '../components/fantasy-football/PlayersTab'
import DraftTab from '../components/fantasy-football/DraftTab'
import RiskTab from '../components/fantasy-football/RiskTab'
import FormulasTab from '../components/fantasy-football/FormulasTab'

const TABS = [
  { id: 'overview', label: 'Overview', icon: '🏈' },
  { id: 'leagues', label: 'Leagues', icon: '🔗' },
  { id: 'players', label: 'Players', icon: '🧢' },
  { id: 'draft', label: 'Draft', icon: '📋' },
  { id: 'risk', label: 'Risk', icon: '🎛️' },
  { id: 'formulas', label: 'Formulas', icon: '📐' },
] as const

type TabId = (typeof TABS)[number]['id']

export default function FantasyFootballPage() {
  const [tab, setTab] = useState<TabId>('overview')
  const state = useFantasy()

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link to="/dashboard" className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <span className="text-3xl">🏈</span>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Fantasy Football</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm">
            Covariance-aware rankings, win-maximizing draft orders, and an auditable formula knowledge base
          </p>
        </div>
      </div>

      <MockDataBanner visible={isFantasyUsingMockData()} />

      {/* Tab bar */}
      <div className="flex gap-1 mb-6 overflow-x-auto pb-1 -mx-1 px-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              tab === t.id
                ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/50 dark:hover:bg-slate-700/50'
            }`}
          >
            <span className="mr-1.5">{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab state={state} onGoToTab={(t) => setTab(t as TabId)} />}
      {tab === 'leagues' && <LeaguesTab state={state} />}
      {tab === 'players' && <PlayersTab state={state} />}
      {tab === 'draft' && <DraftTab state={state} />}
      {tab === 'risk' && <RiskTab state={state} />}
      {tab === 'formulas' && <FormulasTab state={state} />}
    </div>
  )
}
